// oklch-based color palette generator.
// Produces Mantine-compatible 10-shade tuples from a single hex input.
// Pure math — no runtime deps, no DOM, no external libraries.

// ─── sRGB ↔ linear-sRGB ─────────────────────────────────────────────────────

function srgbToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function linearToSrgb(c: number): number {
  return c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055;
}

// ─── linear-sRGB ↔ CIE XYZ (D65) ───────────────────────────────────────────

function linearRgbToXyz(r: number, g: number, b: number): [number, number, number] {
  return [
    0.4124564 * r + 0.3575761 * g + 0.1804375 * b,
    0.2126729 * r + 0.7151522 * g + 0.0721750 * b,
    0.0193339 * r + 0.1191920 * g + 0.9503041 * b,
  ];
}

function xyzToLinearRgb(x: number, y: number, z: number): [number, number, number] {
  return [
    3.2404542 * x - 1.5371385 * y - 0.4985314 * z,
    -0.9692660 * x + 1.8760108 * y + 0.0415561 * z,
    0.0556434 * x - 0.2040259 * y + 1.0572252 * z,
  ];
}

// ─── CIE XYZ ↔ Oklab ───────────────────────────────────────────────────────

function xyzToOklab(x: number, y: number, z: number): [number, number, number] {
  const l_ = 0.8189330101 * x + 0.3618667424 * y - 0.1288597137 * z;
  const m_ = 0.0329845436 * x + 0.9293118715 * y + 0.0361456387 * z;
  const s_ = 0.0482003018 * x + 0.2643662691 * y + 0.6338517070 * z;

  const lc = Math.cbrt(l_);
  const mc = Math.cbrt(m_);
  const sc = Math.cbrt(s_);

  return [
    0.2104542553 * lc + 0.7936177850 * mc - 0.0040720468 * sc,
    1.9779984951 * lc - 2.4285922050 * mc + 0.4505937099 * sc,
    0.0259040371 * lc + 0.7827717662 * mc - 0.8086757660 * sc,
  ];
}

function oklabToXyz(L: number, a: number, b: number): [number, number, number] {
  const lc = L + 0.3963377774 * a + 0.2158037573 * b;
  const mc = L - 0.1055613458 * a - 0.0638541728 * b;
  const sc = L - 0.0894841775 * a - 1.2914855480 * b;

  const l_ = lc * lc * lc;
  const m_ = mc * mc * mc;
  const s_ = sc * sc * sc;

  return [
    1.2270138511 * l_ - 0.5577999807 * m_ + 0.2812561490 * s_,
    -0.0405801784 * l_ + 1.1122568696 * m_ - 0.0716766787 * s_,
    -0.0763812845 * l_ - 0.4214819784 * m_ + 1.5861632204 * s_,
  ];
}

// ─── Oklab ↔ oklch ──────────────────────────────────────────────────────────

type Oklch = { L: number; C: number; h: number };

function oklabToOklch(L: number, a: number, b: number): Oklch {
  const C = Math.sqrt(a * a + b * b);
  const h = C < 1e-8 ? 0 : ((Math.atan2(b, a) * 180) / Math.PI + 360) % 360;
  return { L, C, h };
}

function oklchToOklab(L: number, C: number, h: number): [number, number, number] {
  const hRad = (h * Math.PI) / 180;
  return [L, C * Math.cos(hRad), C * Math.sin(hRad)];
}

// ─── Hex ↔ sRGB ─────────────────────────────────────────────────────────────

function hexToSrgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.slice(0, 2), 16) / 255,
    parseInt(h.slice(2, 4), 16) / 255,
    parseInt(h.slice(4, 6), 16) / 255,
  ];
}

function srgbToHex(r: number, g: number, b: number): string {
  const clamp = (v: number) => Math.max(0, Math.min(1, v));
  const toHex = (v: number) =>
    Math.round(clamp(v) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

// ─── Conversion pipelines ───────────────────────────────────────────────────

function hexToOklch(hex: string): Oklch {
  const [r, g, b] = hexToSrgb(hex);
  const [lr, lg, lb] = [srgbToLinear(r), srgbToLinear(g), srgbToLinear(b)];
  const [x, y, z] = linearRgbToXyz(lr, lg, lb);
  const [L, a, ob] = xyzToOklab(x, y, z);
  return oklabToOklch(L, a, ob);
}

function oklchToHex(L: number, C: number, h: number): string {
  const [oL, oa, ob] = oklchToOklab(L, C, h);
  const [x, y, z] = oklabToXyz(oL, oa, ob);
  const [lr, lg, lb] = xyzToLinearRgb(x, y, z);
  return srgbToHex(linearToSrgb(lr), linearToSrgb(lg), linearToSrgb(lb));
}

// ─── Gamut clamping ─────────────────────────────────────────────────────────

function isInSrgbGamut(L: number, C: number, h: number): boolean {
  const [oL, oa, ob] = oklchToOklab(L, C, h);
  const [x, y, z] = oklabToXyz(oL, oa, ob);
  const [r, g, b] = xyzToLinearRgb(x, y, z);
  const EPS = -0.001;
  const MAX = 1.001;
  return r >= EPS && r <= MAX && g >= EPS && g <= MAX && b >= EPS && b <= MAX;
}

function clampChromaToGamut(L: number, C: number, h: number): number {
  if (isInSrgbGamut(L, C, h)) return C;
  let lo = 0;
  let hi = C;
  while (hi - lo > 0.001) {
    const mid = (lo + hi) / 2;
    if (isInSrgbGamut(L, mid, h)) {
      lo = mid;
    } else {
      hi = mid;
    }
  }
  return lo;
}

// ─── Public API ─────────────────────────────────────────────────────────────

const SHADE_LIGHTNESS = [0.97, 0.93, 0.87, 0.78, 0.68, 0.59, 0.50, 0.42, 0.34, 0.26];

export type ColorTuple = readonly [
  string, string, string, string, string,
  string, string, string, string, string,
];

/**
 * Generate a 10-shade palette from a single hex color.
 * Preserves the input's hue; sweeps lightness in oklch.
 * Each shade is gamut-clamped to sRGB.
 */
export function generatePalette(hex: string): ColorTuple {
  const source = hexToOklch(hex);
  const shades = SHADE_LIGHTNESS.map((targetL) => {
    const C = clampChromaToGamut(targetL, source.C, source.h);
    return oklchToHex(targetL, C, source.h);
  });
  return shades as unknown as ColorTuple;
}

/**
 * Find which shade index (0–9) most closely matches the input hex.
 * Used to set Mantine's primaryShade.
 */
export function findClosestShade(hex: string): number {
  const source = hexToOklch(hex);
  let closest = 0;
  let minDist = Infinity;
  for (let i = 0; i < SHADE_LIGHTNESS.length; i++) {
    const dist = Math.abs(SHADE_LIGHTNESS[i]! - source.L);
    if (dist < minDist) {
      minDist = dist;
      closest = i;
    }
  }
  return closest;
}

export type WarmthTones = {
  bodyBg: string;
  surfaceBg: string;
};

/**
 * Derive warm parchment tones from a brand hex.
 * bodyBg ≈ L=0.96, very low chroma, same hue → sand/parchment.
 * surfaceBg ≈ L=0.98, even lower chroma → cream for cards/inputs.
 */
export function deriveWarmth(hex: string): WarmthTones {
  const source = hexToOklch(hex);
  const bodyC = clampChromaToGamut(0.96, Math.min(source.C, 0.02), source.h);
  const surfaceC = clampChromaToGamut(0.98, Math.min(source.C, 0.012), source.h);
  return {
    bodyBg: oklchToHex(0.96, bodyC, source.h),
    surfaceBg: oklchToHex(0.98, surfaceC, source.h),
  };
}

/**
 * Validate a hex color string.
 */
export function isValidHex(value: string): boolean {
  return /^#[0-9a-fA-F]{6}$/.test(value);
}

/**
 * WCAG 2.1 relative luminance of a hex color.
 */
export function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToSrgb(hex);
  return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b);
}

/**
 * WCAG 2.1 contrast ratio between two hex colors.
 * Returns a value between 1 and 21.
 */
export function contrastRatio(hex1: string, hex2: string): number {
  const l1 = relativeLuminance(hex1);
  const l2 = relativeLuminance(hex2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

// Re-export conversion for service-layer use
export { hexToOklch, oklchToHex };
